import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import api from "../config/axiosConfig";
import { convertToFormData } from "../config/convertToFormData";
import { useRfi } from "../contexts/RfiContext";
import { useSelector } from "react-redux";
import { hasPermission } from "../utils/utilityFunctions";
import { actionType } from "../utils/constant";

export default function useBlasting(stageId, isAPICallAllowed) {
  const { rfinumber } = useParams();
  const { profile } = useSelector((state) => state.profile);
  // Note: Blasting data is now saved to Context at stagesData.blasting.data
  // Can be accessed in other components using: useRfi().stagesData.blasting.data

  const { submitStageAPI, getRFIStages, setBlastingData } = useRfi();
  const [blastingDetails, setBlastingDetails] = useState({});
  const [stageLoading, setStageLoading] = useState(false);

  // Validation Schema
  const validationSchema = Yup.object({
    // Weather conditions validation
    weatherCondition: Yup.string()
      .oneOf(["clear", "rain", "cloudy"], "Invalid weather condition")
      .required("Please select a weather condition"),

    // Blasting specific fields
    surfacePreparation: Yup.string()
      .oneOf(
        ["manual", "power_tool", "abrasive_blast"],
        "Invalid surface preparation"
      )
      .required("Please select a surface preparation method"),
    typeOfAbrasive: Yup.string()
      .trim()
      .required("Type of abrasive is required"),
    surfaceProfile: Yup.number()
      .typeError("Surface profile must be a number")
      .required("Surface profile is required"),
    remarks: Yup.string().trim().required("Remarks are required"),
    pictures: Yup.array()
      .min(1, "Please upload at least one picture")
      .required("Pictures are required"),

    // Ambient & Surface Conditions (nested structure)
    ambientCondition: Yup.object({
      time: Yup.string().trim().required("Time is required"),
      dryBulb: Yup.number()
        .typeError("Must be a valid number")
        .required("Dry bulb temperature is required"),
      wetBulb: Yup.number()
        .typeError("Must be a valid number")
        .required("Wet bulb temperature is required"),
      humidity: Yup.number()
        .typeError("Must be a valid number")
        .min(0, "Humidity must be at least 0%")
        .max(100, "Humidity cannot exceed 100%")
        .required("Humidity is required"),
      dewPoint: Yup.number()
        .typeError("Must be a valid number")
        .required("Dew point is required"),
      substrateTemp: Yup.number()
        .typeError("Must be a valid number")
        .required("Substrate temperature is required"),
      substrateDewPoint: Yup.number()
        .typeError("Must be a valid number")
        .required("Substrate temp minus dew point is required"),
      status: Yup.string().trim(),
      finalInspection: Yup.string()
        .trim()
        .required("Final inspection is required"),
    }),
  });

  // Fetch blasting details for the given rfiId on first render
  useEffect(() => {
    let active = true;
    const fetchBlasting = async () => {
      if (!rfinumber || !isAPICallAllowed) return; // No-op if rfiId not provided
      if (
        !hasPermission(profile?.permissions?.actions, actionType.stage_view)
      ) {
        return;
      }
      try {
        setStageLoading(true);
        const res = await api.get(`/api/blasting/${rfinumber}`);
        if (!active) return;
        const data = res?.data.data || {};
        setBlastingDetails(data);
        // Save to Context
        setBlastingData(data);
      } catch (e) {
        if (!active) return;
        console.error("Failed to fetch blasting details:", e);
        toast.error("Failed to load blasting details. Please try again.");
      } finally {
        setStageLoading(false);
      }
    };
    fetchBlasting();
    return () => {
      active = false;
    };
  }, [
    rfinumber,
    isAPICallAllowed,
    profile?.permissions?.actions,
    setBlastingData,
  ]);

  // Derive initial values from rfiDetails if available
  const initialValues = useMemo(() => {
    const rfiBlasting = blastingDetails.blasting;
    const rfiBlastingImages = blastingDetails?.blasting_images || [];

    if (!rfiBlasting) {
      return {
        weatherCondition: "",
        surfacePreparation: "",
        typeOfAbrasive: "",
        surfaceProfile: "",
        remarks: "",
        pictures: [],
        ambientCondition: {
          time: new Date().toLocaleDateString(),
          dryBulb: "",
          wetBulb: "",
          humidity: "",
          dewPoint: "",
          substrateTemp: "",
          substrateDewPoint: "",
          status: "Blast",
          finalInspection: "",
        },
      };
    }

    // Determine weatherCondition radio from booleans
    let weatherCondition = "";
    if (rfiBlasting.clear) weatherCondition = "clear";
    else if (rfiBlasting.rain) weatherCondition = "rain";
    else if (rfiBlasting.cloudy) weatherCondition = "cloudy";

    // Determine surfacePreparation radio from booleans
    let surfacePreparation = "";
    if (rfiBlasting.manual) surfacePreparation = "manual";
    else if (rfiBlasting.power_tool) surfacePreparation = "power_tool";
    else if (rfiBlasting.abrasive_blast) surfacePreparation = "abrasive_blast";

    // If backend provides image objects, pass them through; UploadPictures
    // normalizes display for { image_id, image_url, ... }
    const pictures = (
      Array.isArray(rfiBlastingImages) ? rfiBlastingImages : []
    ).filter(Boolean);

    return {
      weatherCondition,
      surfacePreparation,
      typeOfAbrasive: rfiBlasting.type_of_abrasive || "",
      surfaceProfile: rfiBlasting.surface_profile || "",
      remarks: rfiBlasting.remarks || "",
      pictures,
      ambientCondition: {
        time: new Date(rfiBlasting.created_at).toLocaleDateString(),
        dryBulb: rfiBlasting.dry_bulb ?? "",
        wetBulb: rfiBlasting.wet_bulb ?? "",
        humidity: rfiBlasting.humidity ?? "",
        dewPoint: rfiBlasting.dew_point ?? "",
        substrateTemp: rfiBlasting.substrate_temp ?? "",
        substrateDewPoint: rfiBlasting.substrate_temp_minus_dew_point ?? "",
        status: rfiBlasting.status || "Blast",
        finalInspection: rfiBlasting.final_inspection || "",
      },
    };
  }, [blastingDetails]);

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      return;
    }
    setSubmitting(true);
    try {
      const blasting_id = blastingDetails?.blasting?.blasting_id;

      // Map Formik values to API payload
      const payload = {
        blasting_id,
        rfi_id: blastingDetails?.rfi_id,
        // Weather condition
        clear: values?.weatherCondition === "clear",
        cloudy: values?.weatherCondition === "cloudy",
        rain: values?.weatherCondition === "rain",

        // Blasting methods
        manual: values?.surfacePreparation === "manual",
        power_tool: values?.surfacePreparation === "power_tool",
        abrasive_blast: values?.surfacePreparation === "abrasive_blast",

        // Optional text fields
        type_of_abrasive: values?.typeOfAbrasive,
        surface_profile: Number(values?.surfaceProfile),
        remarks: values?.remarks,

        // Ambient & Surface conditions
        dry_bulb: Number(values?.ambientCondition?.dryBulb),
        wet_bulb: Number(values?.ambientCondition?.wetBulb),
        humidity: Number(values?.ambientCondition?.humidity),
        dew_point: Number(values?.ambientCondition?.dewPoint),
        substrate_temp: Number(values?.ambientCondition?.substrateTemp),
        substrate_temp_minus_dew_point: Number(
          values?.ambientCondition?.substrateDewPoint
        ),
        status: values?.ambientCondition?.status,
        final_inspection: values?.ambientCondition?.finalInspection,

        // Include both new File instances and existing images
        images: values?.pictures.map((each) => {
          if (each.file instanceof File) {
            return each.file;
          }
          return each;
        }),
      };

      const FormData = convertToFormData(payload);
      const res = await api.post("/api/blasting/", FormData);
      // Success toast
      const successMsg =
        res?.data?.message || "Blasting details submitted successfully";
      toast.success(successMsg);

      // Refetch and update Context after successful submission
      const updatedRes = await api.get(`/api/blasting/${rfinumber}`);
      const updatedData = updatedRes?.data.data || {};
      setBlastingDetails(updatedData);

      setBlastingData(updatedData);
    } catch (error) {
      console.error("Failed to submit blasting:", error);
      // Error toast with server message fallback
      const errMsg =
        error?.response?.data?.message ||
        "Failed to submit blasting details. Please try again.";
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
      await submitStageAPI({ stage_id: stageId });
      await getRFIStages(rfinumber);
    }
  };

  const createSendForApprovalHandler = (
    validateForm,
    submitForm,
    setTouched
  ) => {
    return async () => {
      // Mark all fields as touched to show validation errors
      setTouched({
        weatherCondition: true,
        surfacePreparation: true,
        typeOfAbrasive: true,
        surfaceProfile: true,
        remarks: true,
        pictures: true,
        ambientCondition: {
          time: true,
          dryBulb: true,
          wetBulb: true,
          humidity: true,
          dewPoint: true,
          substrateTemp: true,
          substrateDewPoint: true,
          status: true,
          finalInspection: true,
        },
      });

      const errors = await validateForm();
      if (Object.keys(errors).length === 0) {
        submitForm();
      } else {
        console.error("Validation errors:", errors);
        // Auto-scroll to first error field
        setTimeout(() => {
          const firstErrorElement = document.querySelector(
            ".Mui-error input, .Mui-error textarea"
          );
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            firstErrorElement.focus();
          }
        }, 100);
      }
    };
  };

  return {
    validationSchema,
    initialValues,
    stageLoading,
    handleSubmit,
    createSendForApprovalHandler,
  };
}
